import { Stack, Box, Typography } from '@mui/material';
import { ReactComponent as VivicaLogo } from './assets/logo24.svg';
import { ReactComponent as DownloadButton } from './assets/AppDownloadButton.svg';
import './b2c.scss';
import { useEffect, useMemo, useState } from 'react';
import { PulseLoader } from 'react-spinners';

const classes = {
  signature: {
    color: '#4B5563',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '22px',
  },
  loader: {
    '@keyframes react-spinners-PulseLoader-pulse': {
      '0%': {
        transform: 'scale(1)',
        opacity: 1,
      },
      '45%': {
        transform: 'scale(0.1)',
        opacity: 0.7,
      },
      '80%': {
        transform: 'scale(1)',
        opacity: 1,
      },
    },
  },
};

interface INotifocationTaskInfo {
  selectionId: number;
  subject: string;
  description: string;
  notificationPriority: NotificationPriority;
  attachmentFileId: string;
  creatorFirstName: string;
  creatorLastName: string;
  creatorMiddleName: string;
  creatorCompanyName: string;
  creatorCompanyAddress: string;
}

enum NotificationPriority {
  Low = 'Low',
  Medium = 'Medium',
  High = 'High',
}

const separator = 'Best regards,';
export const B2c = () => {
  const [taskInfo, setTaskInfo] = useState<Nullable<INotifocationTaskInfo>>(null);

  const [imageUrl, setImageUrl] = useState('');

  const [loading, setLoading] = useState(false);
  const [loadImage, setLoadImage] = useState(false);

  useEffect(() => {
    const query = new URLSearchParams(window.location.search);

    const getData = async () => {
      const responseInfo = await fetch(`/api/v1/notificationtaskinfo/${query.get('id')}`);
      const notificationTaskInfo: INotifocationTaskInfo = await responseInfo.json();

      setTaskInfo(notificationTaskInfo);
      setImageUrl(
        notificationTaskInfo.attachmentFileId
          ? `/api/v1/notificationtaskinfo/attachment-files/${notificationTaskInfo.attachmentFileId}`
          : '',
      );
      setLoading(false);
      setLoadImage(!!notificationTaskInfo.attachmentFileId);
      if (!notificationTaskInfo.attachmentFileId) {
        setLoading(false);
      }
    };

    void getData();
  }, []);

  const onLoadImage = () => {
    setLoadImage(false);
  };

  const backgroundPriority = useMemo(() => {
    if (!taskInfo) return '';
    const { notificationPriority } = taskInfo;

    if (notificationPriority === NotificationPriority.High) return '#FDECEF';
    if (notificationPriority === NotificationPriority.Medium) return '#FEF7E8';
    return '#E6F8F7';
  }, [taskInfo]);

  const description = taskInfo?.description.split(separator)?.[0] || '';

  const signature = taskInfo?.description.split(separator)?.[1] || '';

  return (
    <>
      <Stack height="100%" className="stack">
        <Box className="wrapper">
          <VivicaLogo />
          <Typography
            color="#374151"
            fontWeight={700}
            fontSize="20px"
            lineHeight="24px"
            component="p"
            mb="14px"
            mt="54px"
          >
            {taskInfo?.subject}
          </Typography>
          <Box height="1px" sx={{ background: '#BFE5EB' }} mb="40px" />
          <Stack
            direction="row"
            gap="12px"
            padding="6px 0"
            width="100%"
            sx={{ background: backgroundPriority }}
            mb="30px"
          >
            <Typography color="#4B5563" fontSize="16px" fontWeight={700} lineHeight="24px">
              Priority
            </Typography>
            <Typography>{taskInfo?.notificationPriority}</Typography>
          </Stack>
          {description.split('<br>').map((val, index, arr) => (
            <Typography
              color="#6B7280"
              fontSize="16px"
              lineHeight="24px"
              fontWeight={400}
              mb={index === arr.length - 1 ? '36px' : 0}
            >
              {val || ' '}
            </Typography>
          ))}

          {signature && (
            <Typography mb="6px" component="p" sx={classes.signature}>
              Best regards,
            </Typography>
          )}
          {signature.split('<br>').map(val => (
            <Typography sx={classes.signature}>{val || ' '}</Typography>
          ))}
          {loadImage && (
            <Stack
              justifyContent="center"
              alignItems="center"
              height="100%"
              sx={{
                height: '120px',
                width: '180px',
                ...classes.loader,
              }}
            >
              <PulseLoader color="#0096b2" size={10} />
            </Stack>
          )}
          {imageUrl && (
            <Box mt="36px">
              <img
                style={{ objectFit: 'cover', ...(loadImage ? { display: 'none' } : {}) }}
                src={imageUrl}
                onLoad={onLoadImage}
                alt="img"
                width="180px"
                height="120px"
                decoding="sync"
              />
            </Box>
          )}

          <Typography
            color="grey.700"
            lineHeight="18px"
            component="p"
            mt={imageUrl ? '42px' : '36px'}
            mb="18px"
            fontSize="14px"
          >
            Download the Vivica app:
          </Typography>
          <a
            target="_blank"
            rel="noreferrer"
            href="https://apps.apple.com/us/app/vivica-patient/id6471080701?platform=iphone"
            style={{ padding: 0, textDecoration: 'none' }}
          >
            <DownloadButton />
          </a>
        </Box>
      </Stack>

      {loading && (
        <Stack
          justifyContent="center"
          alignItems="center"
          height="100%"
          sx={{
            position: 'absolute',
            top: '0',
            left: '0',
            height: '100vh',
            width: '100vw',
            background: '#fff',
            zIndex: 100,
            ...classes.loader,
          }}
        >
          <PulseLoader color="#0096b2" size={20} />
        </Stack>
      )}
    </>
  );
};
